import { ChakraProvider, Flex, Link, Text } from "@chakra-ui/react";
import "moment/dist/locale/nb";
import React, { ReactNode, useLayoutEffect } from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter, useLocation } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";
import "./i18n";
import { App } from "./app";
import { BundleVersionWatcher } from "./common/bundle-version/BundleVersionWatcher";
import { ErrorBoundary } from "./common/errors/ErrorBoundary/ErrorBoundary";
import { store } from "./common/redux/store";
import { bundleCommit, theme as chakraTheme, requireStringEnvVar } from "./config";
import { ddRum } from "./datadog";

ddRum.init({
  applicationId: requireStringEnvVar("VITE_DATADOG_APPLICATION_ID"),
  clientToken: requireStringEnvVar("VITE_DATADOG_CLIENT_TOKEN"),
  site: "datadoghq.eu",
  service: "sm-webapp",
  env: requireStringEnvVar("VITE_DATADOG_ENV"),
  version: bundleCommit,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 20,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: "mask-user-input",
});

const ScrollToTopWrapper = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [location.pathname]);
  return <>{children}</>;
};

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <ChakraProvider theme={chakraTheme}>
    <ErrorBoundary
      fallback={
        <Flex
          direction="column"
          align="center"
          justify="center"
          minH="100vh"
          p={8}
        >
          <Text fontSize="lg" mb={4}>
            Sorry, that was not supposed to happen. Our team has been notified.
          </Text>
          <Text>
            You can{" "}
            <Link color="smPrimary" href="/">
              click here
            </Link>{" "}
            to go back to the home page.
          </Text>
        </Flex>
      }
      scope="application"
    >
      <Provider store={store}>
        <React.StrictMode>
          <BrowserRouter>
            <BundleVersionWatcher bundleCommit={bundleCommit} />
            <ScrollToTopWrapper>
              <IntercomProvider
                appId={requireStringEnvVar("VITE_INTERCOM_APP_ID")}
                apiBase={requireStringEnvVar("VITE_INTERCOM_BASE_URL")}
                autoBoot={true}
              >
                <App />
              </IntercomProvider>
            </ScrollToTopWrapper>
          </BrowserRouter>
        </React.StrictMode>
      </Provider>
    </ErrorBoundary>
  </ChakraProvider>
);
